import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { Button } from '../../../components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { AppIcon } from '../../../components/ui/icon'
import { mdiFileExport, mdiFileImport, mdiFolderOutline, mdiPencil, mdiShape, mdiBriefcase, mdiFileSign, mdiAccountDetails, mdiFolder, mdiFunction, mdiAutoFix, mdiSwapHorizontal, mdiShieldCheck, mdiScale, mdiSync, mdiPalette } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { OntologyDriftNotice } from '../../../components/ontology/OntologyDriftNotice'

export const Route = createFileRoute('/$org/types/')({
  component: TypesIndex,
})

function TypesIndex() {
  const { org } = useParams({ from: '/$org/types/' })
  const { t } = useTranslation('types')

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb.types'), path: `/${org}/types` }
  ])

  const typesFeatures = [
    {
      title: t('features.export_title'),
      description: t('features.export_description'),
      icon: mdiFileExport,
      href: `/${org}/types/export`,
      action: t('common:actions.export'),
      available: true,
    },
    {
      title: t('features.import_title'),
      description: t('features.import_description'),
      icon: mdiFileImport,
      href: `/${org}/types/import`,
      action: t('common:actions.import'),
      available: true,
    },
    {
      title: t('features.sync_title'),
      description: t('features.sync_description'),
      icon: mdiSync,
      href: `/${org}/types/sync`,
      action: t('features.sync_action'),
      available: true,
      testId: 'types-sync-card-btn',
    },
    {
      title: t('features.thematics_title'),
      description: t('features.thematics_description'),
      icon: mdiPalette,
      href: `/${org}/types/thematics`,
      action: t('features.thematics_action'),
      available: true,
      testId: 'types-thematics-card-btn',
    },
  ]

  const typeCategories = [
    { icon: mdiPencil, label: t('categories.properties.label'), description: t('categories.properties.description') },
    { icon: mdiShape, label: t('categories.asset_types.label'), description: t('categories.asset_types.description') },
    { icon: mdiBriefcase, label: t('categories.document_types.label'), description: t('categories.document_types.description') },
    { icon: mdiFileSign, label: t('categories.contract_types.label'), description: t('categories.contract_types.description') },
    { icon: mdiAccountDetails, label: t('categories.contract_stakeholders.label'), description: t('categories.contract_stakeholders.description') },
    { icon: mdiFolder, label: t('categories.folder_layouts.label'), description: t('categories.folder_layouts.description') },
    { icon: mdiFunction, label: t('categories.derivations.label'), description: t('categories.derivations.description') },
    { icon: mdiAutoFix, label: t('categories.normalizations.label'), description: t('categories.normalizations.description') },
    { icon: mdiSwapHorizontal, label: t('categories.property_mapping_rules.label'), description: t('categories.property_mapping_rules.description') },
    { icon: mdiShieldCheck, label: t('categories.compliance_controls.label'), description: t('categories.compliance_controls.description') },
    { icon: mdiScale, label: t('categories.compliance_rules.label'), description: t('categories.compliance_rules.description') },
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        icon={SECTION_ICONS.types}
        title={t('page_title')}
        description={t('page_description')}
      />

      <OntologyDriftNotice org={org} />

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {typesFeatures.map((feature) => (
          <Card key={feature.title} className={`${!feature.available ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={feature.icon} size="lg" className="text-primary" />
                {feature.title}
                {!feature.available && (
                  <span className="text-xs bg-muted px-2 py-1 rounded">{t('features.coming_soon')}</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {feature.description}
              </p>
              <Button
                asChild
                disabled={!feature.available}
                className="w-full"
              >
                <Link to={feature.href} data-testid={feature.testId}>
                  {feature.action}
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Information */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AppIcon path={mdiFolderOutline} size="lg" />
            {t('categories.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {typeCategories.map((cat) => (
              <div key={cat.label}>
                <strong className="flex items-center gap-1.5">
                  <AppIcon path={cat.icon} size="sm" />
                  {cat.label}
                </strong>
                <p className="text-muted-foreground mt-1">
                  {cat.description}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
